import { z } from 'zod'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import * as duration from 'duration-fns'
import { durationNotation, durationToHHHMM } from '@/lib/utils/time-notation-HHH'

import { graphql } from '@/gql'
import type { Coassign_Work_On_Project_Insert_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'

import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'
import { type RawBuilder, sql } from 'kysely'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

function json<T>(value: T): RawBuilder<T> {
  return sql`${JSON.stringify(value)}`
}

// 日付形式の正規表現パターン (YYYY-MM-DD)
const DATE_REGEX_PATTERN = '[0-9]{4}-[0-9]{2}-[0-9]{2}'
// 日付キーのスキーマ (完全一致)
const dateKeySchema = z.string().regex(new RegExp(`^${DATE_REGEX_PATTERN}$`))
// NOTE
// モデル層がないため、schemaのバリデーションをより厳密に定義するチーム意見(特にjsonbなど)
export const schema = z.object({
  // contextual values
  id: z.string().length(21),
  project_id: z.string().length(21).refine(isExist('project'), errorParentIsRemoved),
  user_id: z.string().length(21),
  assign_id: z.string().length(21).refine(isExist('assign'), errorParentIsRemoved),
  month: z.string().regex(new RegExp(`^${DATE_REGEX_PATTERN}$`)),
  // can modifies
  actuals: z.record(dateKeySchema, durationNotation.default('')),
  comments: z.record(dateKeySchema, z.string().max(255)).default({})
})

export type WorkOnProject = z.infer<typeof schema>

export class WorkOnProjectMutator extends Mutator<typeof schema> {
  static readonly stageableClassName = 'WorkOnProjectMutator'
  schema = schema
  table = 'work_on_project' as const

  static async load(
    id: string,
    context: Pick<WorkOnProject, 'project_id' | 'user_id' | 'assign_id' | 'month'>
  ): Promise<Reactive<WorkOnProjectMutator>> {
    const self = this.createInstance()
    // // FIXME: need use watch query
    const record = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    // レコードが存在しない場合は早期リターン / Early return if record doesn't exist
    if (!record) {
      self.base = {
        ...context,
        ...{ id },
        month: context['month'],
        actuals: {},
        comments: {}
      }
      self.context = { ...context, ...{ id } }
      return self
    }

    // レコード存在時の処理 / Process when record exists
    const baseActuals = Object.fromEntries(
      Object.entries(record.actuals).map(([date, value]) => [
        date,
        durationToHHHMM({ seconds: value })
      ])
    )

    // comments は Record<string, string> として取得 / Comments are directly available as Record
    const baseComments = record.comments ?? {}

    self.base = {
      ...context,
      ...{ id },
      month: context['month'],
      actuals: baseActuals,
      comments: baseComments
    }
    self.context = { ...context, ...{ id } }
    return self
  }

  convertActualsToSeconds(actuals: Record<string, Duration>) {
    return Object.fromEntries(
      Object.entries(actuals).map(([date, value]) => [date, duration.toSeconds(value)])
    )
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => {
        if (key === 'actuals') return [key, json(this.convertActualsToSeconds(rawData.actuals))]
        if (key === 'comments') return [key, json(value)]

        return [key, typeof value === 'boolean' ? Number(value) : value]
      })
    )
    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  buildInsertParams(params: z.output<typeof schema>) {
    const tenant_id = useUserStore().userInfo!.tenantId
    return {
      tenant_id,
      id: params.id,
      user_id: params.user_id,
      project_id: params.project_id,
      assign_id: params.assign_id,
      month: params.month,
      actuals: json(this.convertActualsToSeconds(params.actuals)),
      comments: json(params.comments)
    }
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const params = (await this.parsed)!
    const sqliteParams = this.buildInsertParams(params)
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    throw Error('FIXME: not implemented')
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const gqlparams = {
      id: params.id,
      user_id: params.user_id,
      project_id: params.project_id,
      assign_id: params.assign_id,
      month: params.month,
      actuals: this.convertActualsToSeconds(params.actuals),
      comments: params.comments
    }
    const variables: Coassign_Work_On_Project_Insert_Input = { ...gqlparams, ...{ commit_id } }
    return [{ mutation: InsertWorkOnProjectDocument, variables: { input: variables } }]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const params = (await this.parsed)!
    const variables = {
      id,
      commit_id,
      actuals: this.convertActualsToSeconds(params.actuals),
      comments: params.comments
    }
    return [{ mutation: UpdateWorkOnProjectDocument, variables }]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asDeleteMutation(_commitId: string): Promise<MutationOperationAndVariables[]> {
    throw Error('FIXME: not implemented')
  }
}

const InsertWorkOnProjectDocument = graphql(/* GraphQL */ `
  mutation insertWorkOnProject($input: coassign_work_on_project_insert_input!) {
    insert_coassign_work_on_project_one(object: $input) {
      created_at
    }
  }
`)

const UpdateWorkOnProjectDocument = graphql(/* GraphQL */ `
  mutation updateWorkOnProject(
    $id: String!
    $commit_id: String!
    $actuals: jsonb!
    $comments: jsonb
  ) {
    update_coassign_work_on_project_by_pk(
      pk_columns: { id: $id }
      _set: { commit_id: $commit_id, comments: $comments }
      _append: { actuals: $actuals }
    ) {
      updated_at
    }
  }
`)
